import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X, Play, Upload, MessageSquare, Globe, BarChart3,
  Video, Shield, Target, Activity, Crosshair, Zap,
  Send, Loader2, FileText,
  Search, Clock, AlertCircle, CheckCircle,
  Film, Brain
} from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

// ═══════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

// Rating circle (adapted from NILPlayerCard)
const RatingCircle = ({ rating, label, size = 'md', gradientId }) => {
  const dims = size === 'lg' ? { w: 80, r: 34, sw: 5, text: 'text-2xl' }
    : size === 'sm' ? { w: 48, r: 20, sw: 3, text: 'text-sm' }
    : { w: 64, r: 28, sw: 4, text: 'text-lg' };
  const gid = gradientId || `grade-${label?.replace(/\s/g, '')}`;
  const circumference = 2 * Math.PI * dims.r;
  const dashLen = rating != null ? (rating / 100) * circumference : 0;

  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative" style={{ width: dims.w, height: dims.w }}>
        <svg className="w-full h-full transform -rotate-90" viewBox={`0 0 ${dims.w} ${dims.w}`}>
          <circle
            cx={dims.w / 2} cy={dims.w / 2} r={dims.r}
            fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={dims.sw}
          />
          <circle
            cx={dims.w / 2} cy={dims.w / 2} r={dims.r}
            fill="none" stroke={`url(#${gid})`} strokeWidth={dims.sw}
            strokeLinecap="round"
            strokeDasharray={`${dashLen} ${circumference}`}
            className="transition-all duration-1000 ease-out"
          />
          <defs>
            <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#E8B800" />
              <stop offset="100%" stopColor="#FF6B00" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`${dims.text} font-black text-white`}>
            {rating != null ? Math.round(rating) : '--'}
          </span>
        </div>
      </div>
      {label && <span className="text-[10px] text-gray-400 uppercase tracking-wider text-center">{label}</span>}
    </div>
  );
};

// Stat bar (from NILPlayerCard)
const StatBar = ({ label, value, max = 100, color = '#E8B800' }) => {
  const percentage = Math.min((value / max) * 100, 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center">
        <span className="text-xs text-gray-400 uppercase tracking-wider">{label}</span>
        <span className="text-sm font-bold text-white">{value != null ? Math.round(value) : '--'}</span>
      </div>
      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-1000 ease-out"
          style={{
            width: `${percentage}%`,
            background: `linear-gradient(90deg, ${color}, ${color}88)`,
          }}
        />
      </div>
    </div>
  );
};

// Lens icon mapping
const LENS_ICONS = {
  offensive: Target,
  defensive: Shield,
  performance: Activity,
  tactical: Brain,
  biomechanics: Zap,
  shooting: Crosshair,
};

const LENS_COLORS = {
  offensive: '#FF6B00',
  defensive: '#00D4FF',
  performance: '#00FF88',
  tactical: '#E8B800',
  biomechanics: '#FF44CC',
  shooting: '#8B5CF6',
};

// ═══════════════════════════════════════════════════════════════════════════
// TAB PANELS
// ═══════════════════════════════════════════════════════════════════════════

// Video Tab — segments list with embedded player
const VideoTab = ({ segments, loading }) => {
  const [selectedSegment, setSelectedSegment] = useState(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-yellow-400 animate-spin" />
        <span className="ml-2 text-gray-400">Loading segments...</span>
      </div>
    );
  }

  if (!segments || segments.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Film className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p className="text-sm">No video segments found for this player.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Embedded video player */}
      {selectedSegment?.video_url && (
        <div className="rounded-xl overflow-hidden border border-white/10 bg-black">
          <video
            src={selectedSegment.video_url}
            controls
            autoPlay
            className="w-full max-h-64 object-contain bg-black"
          />
          <div className="px-3 py-2 bg-gray-900/80 border-t border-white/10">
            <p className="text-xs text-gray-300">{selectedSegment.description || selectedSegment.play_type || 'Clip'}</p>
          </div>
        </div>
      )}

      {/* Segment list */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
        {segments.map((seg, i) => {
          const isActive = selectedSegment?.segment_id === seg.segment_id;
          return (
            <button
              key={`${seg.segment_id}-${i}`}
              onClick={() => setSelectedSegment(seg)}
              className={`w-full text-left p-3 rounded-xl border transition-all ${
                isActive
                  ? 'bg-yellow-500/10 border-yellow-500/30'
                  : 'bg-white/5 border-white/10 hover:bg-white/10'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Play className="w-3 h-3 text-yellow-400 flex-shrink-0" />
                    <span className="text-sm font-medium text-white truncate">
                      {seg.play_type || 'Play'}
                    </span>
                    {seg.quarter && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-gray-400">
                        Q{seg.quarter}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1 truncate">
                    {seg.game_name || 'Unknown Game'}
                  </p>
                  {seg.start_time && (
                    <p className="text-[10px] text-gray-600 flex items-center gap-1 mt-0.5">
                      <Clock className="w-3 h-3" />
                      {seg.start_time}{seg.end_time ? ` - ${seg.end_time}` : ''}
                    </p>
                  )}
                </div>

                {/* Mini lens scores */}
                <div className="flex gap-1 flex-shrink-0">
                  {seg.composite_grade && (
                    <div className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                      seg.composite_grade >= 80 ? 'bg-green-500/20 text-green-400'
                      : seg.composite_grade >= 60 ? 'bg-yellow-500/20 text-yellow-400'
                      : 'bg-red-500/20 text-red-400'
                    }`}>
                      {Math.round(seg.composite_grade)}
                    </div>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Stats Tab
const StatsTab = ({ card }) => {
  if (!card) {
    return (
      <div className="text-center py-12 text-gray-500">
        <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p className="text-sm">No stats data available.</p>
      </div>
    );
  }

  const grades = card.grades || {};

  return (
    <div className="space-y-6">
      {/* Core metrics */}
      <div className="grid grid-cols-3 gap-2">
        <div className="bg-white/5 rounded-lg p-3 text-center border border-white/10">
          <p className="text-lg font-black text-white">{card.segment_count || 0}</p>
          <p className="text-[10px] text-gray-400 uppercase">Segments</p>
        </div>
        <div className="bg-white/5 rounded-lg p-3 text-center border border-white/10">
          <p className="text-lg font-black text-white">{card.analysis_count || 0}</p>
          <p className="text-[10px] text-gray-400 uppercase">Analyses</p>
        </div>
        <div className="bg-white/5 rounded-lg p-3 text-center border border-white/10">
          <p className="text-lg font-black text-white">
            {grades.composite != null ? Math.round(grades.composite) : '--'}
          </p>
          <p className="text-[10px] text-gray-400 uppercase">Composite</p>
        </div>
      </div>

      {/* Lens grade bars */}
      <div className="space-y-3">
        <p className="text-xs text-gray-400 uppercase tracking-wider">Lens Grades</p>
        <StatBar label="Offensive" value={grades.offensive} color={LENS_COLORS.offensive} />
        <StatBar label="Defensive" value={grades.defensive} color={LENS_COLORS.defensive} />
        <StatBar label="Performance" value={grades.performance} color={LENS_COLORS.performance} />
        <StatBar label="Tactical" value={grades.tactical} color={LENS_COLORS.tactical} />
        <StatBar label="Biomechanics" value={grades.biomechanics} color={LENS_COLORS.biomechanics} />
        <StatBar label="Shooting" value={grades.shooting} color={LENS_COLORS.shooting} />
      </div>

      {/* Raw card fields if available */}
      {card.ppg != null && (
        <div className="space-y-2">
          <p className="text-xs text-gray-400 uppercase tracking-wider">Traditional Stats</p>
          <div className="grid grid-cols-3 gap-2">
            {['ppg', 'rpg', 'apg', 'spg', 'bpg', 'mpg'].map((stat) =>
              card[stat] != null ? (
                <div key={stat} className="bg-white/5 rounded-lg p-2 text-center border border-white/10">
                  <p className="text-sm font-bold text-white">{card[stat]}</p>
                  <p className="text-[10px] text-gray-500 uppercase">{stat}</p>
                </div>
              ) : null
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// FIT Score Tab — SLAM FIT Score v2 with 6-lens radar
const LENS_CONFIG = [
  { key: 'athleticPerformance', label: 'Athletic', color: '#22c55e' },
  { key: 'statisticalProduction', label: 'Statistical', color: '#3b82f6' },
  { key: 'characterLeadership', label: 'Character', color: '#a855f7' },
  { key: 'cognitiveProcessing', label: 'Cognitive', color: '#f59e0b' },
  { key: 'developmentTrajectory', label: 'Development', color: '#06b6d4' },
  { key: 'slamComposite', label: 'SLAM', color: '#E8B800' },
];

const FitScoreRadar = ({ lenses }) => {
  const cx = 100, cy = 100, r = 75;
  const n = LENS_CONFIG.length;
  const angleStep = (2 * Math.PI) / n;

  const getPoint = (i, val) => {
    const angle = -Math.PI / 2 + i * angleStep;
    const dist = (val / 100) * r;
    return { x: cx + dist * Math.cos(angle), y: cy + dist * Math.sin(angle) };
  };

  const polyPoints = LENS_CONFIG.map((l, i) => {
    const score = lenses?.[l.key]?.score || 0;
    const pt = getPoint(i, score);
    return `${pt.x},${pt.y}`;
  }).join(' ');

  return (
    <svg viewBox="0 0 200 200" className="w-full max-w-[240px] mx-auto">
      {/* Grid rings */}
      {[25, 50, 75, 100].map(v => (
        <polygon key={v} points={
          Array.from({ length: n }, (_, i) => {
            const pt = getPoint(i, v);
            return `${pt.x},${pt.y}`;
          }).join(' ')
        } fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />
      ))}
      {/* Axis lines */}
      {LENS_CONFIG.map((_, i) => {
        const pt = getPoint(i, 100);
        return <line key={i} x1={cx} y1={cy} x2={pt.x} y2={pt.y} stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" />;
      })}
      {/* Data polygon */}
      <polygon points={polyPoints} fill="rgba(232,184,0,0.15)" stroke="#E8B800" strokeWidth="1.5" />
      {/* Data points + labels */}
      {LENS_CONFIG.map((l, i) => {
        const score = lenses?.[l.key]?.score || 0;
        const pt = getPoint(i, score);
        const labelPt = getPoint(i, 115);
        return (
          <g key={l.key}>
            <circle cx={pt.x} cy={pt.y} r="3" fill={l.color} />
            <text x={labelPt.x} y={labelPt.y} textAnchor="middle" dominantBaseline="middle"
              fill="rgba(255,255,255,0.5)" fontSize="6" fontWeight="600">
              {l.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

const AnimatedScore = ({ target }) => {
  const [current, setCurrent] = useState(0);
  useEffect(() => {
    if (target == null) return;
    let frame;
    const start = performance.now();
    const duration = 1200;
    const animate = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setCurrent(Math.round(eased * target));
      if (t < 1) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [target]);
  return <span>{current}</span>;
};

const FitScoreTab = ({ playerName }) => {
  const [fitData, setFitData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateFitScore = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch('/api/player/fit-score', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId: playerName, sport: 'basketball' }),
      });
      const json = await resp.json();
      if (json.success && json.data?.fitScore) {
        setFitData(json.data.fitScore);
      } else {
        setError(json.error?.message || 'Failed to generate FIT Score');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!fitData && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-20 h-20 rounded-full border-2 border-yellow-500/30 flex items-center justify-center mb-4 bg-yellow-500/5">
          <Zap className="w-8 h-8 text-yellow-400" />
        </div>
        <h3 className="text-lg font-bold text-white mb-2">SLAM FIT Score</h3>
        <p className="text-sm text-gray-400 text-center mb-6 max-w-xs">
          Generate a comprehensive 6-lens player evaluation using all available media and data.
        </p>
        <button
          onClick={generateFitScore}
          className="px-6 py-2.5 bg-gradient-to-r from-yellow-500 to-amber-600 text-black font-bold rounded-xl hover:from-yellow-400 hover:to-amber-500 transition-all"
        >
          Generate SLAM FIT Score
        </button>
        {error && (
          <p className="mt-3 text-xs text-red-400 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> {error}
          </p>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-yellow-400 animate-spin mb-4" />
        <p className="text-sm text-gray-400">Analyzing all player data...</p>
        <p className="text-xs text-gray-600 mt-1">This may take 15-30 seconds</p>
      </div>
    );
  }

  const { lenses, overallFitScore, fitTier, playerSummary, keyStrengths, developmentAreas, confidence } = fitData;

  return (
    <div className="space-y-6">
      {/* Overall score hero */}
      <div className="text-center">
        <div className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-500/10 border border-yellow-500/30 rounded-full mb-3">
          <Zap className="w-3 h-3 text-yellow-400" />
          <span className="text-[10px] text-yellow-400 font-bold uppercase tracking-wider">SLAM FIT Score</span>
        </div>
        <div className="text-6xl font-black text-white mb-1">
          <AnimatedScore target={overallFitScore} />
        </div>
        <div className={`text-sm font-bold uppercase tracking-wider ${
          overallFitScore >= 80 ? 'text-green-400' : overallFitScore >= 60 ? 'text-yellow-400' : 'text-red-400'
        }`}>
          {fitTier}
        </div>
        <p className="text-[10px] text-gray-500 mt-1">{confidence}% confidence</p>
      </div>

      {/* Radar chart */}
      <FitScoreRadar lenses={lenses} />

      {/* Lens breakdown */}
      <div className="space-y-2">
        {LENS_CONFIG.map(({ key, label, color }) => {
          const lens = lenses?.[key];
          if (!lens) return null;
          return (
            <div key={key} className="bg-white/5 rounded-lg p-3 border border-white/10">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-medium text-white">{label}</span>
                <span className="text-sm font-bold" style={{ color }}>{lens.score}</span>
              </div>
              <div className="w-full h-1 bg-white/10 rounded-full mb-2">
                <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${lens.score}%`, backgroundColor: color }} />
              </div>
              <p className="text-[10px] text-gray-400">{lens.narrative}</p>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      {playerSummary && (
        <div className="bg-white/5 rounded-lg p-4 border border-white/10">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Executive Summary</p>
          <p className="text-sm text-gray-300 leading-relaxed">{playerSummary}</p>
        </div>
      )}

      {/* Strengths & Development */}
      <div className="grid grid-cols-2 gap-3">
        {keyStrengths?.length > 0 && (
          <div>
            <p className="text-[10px] text-green-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" /> Strengths
            </p>
            {keyStrengths.map((s, i) => (
              <p key={i} className="text-[11px] text-gray-400 mb-1">• {s}</p>
            ))}
          </div>
        )}
        {developmentAreas?.length > 0 && (
          <div>
            <p className="text-[10px] text-amber-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <Target className="w-3 h-3" /> Development
            </p>
            {developmentAreas.map((d, i) => (
              <p key={i} className="text-[11px] text-gray-400 mb-1">• {d}</p>
            ))}
          </div>
        )}
      </div>

      {/* Regenerate */}
      <button
        onClick={generateFitScore}
        disabled={loading}
        className="w-full py-2 text-xs text-gray-400 hover:text-white border border-white/10 rounded-lg hover:bg-white/5 transition-colors"
      >
        Regenerate Score
      </button>
    </div>
  );
};

// Upload Tab — drag-and-drop zone (follows UploadTrainingStats pattern)
const UploadTab = ({ playerName }) => {
  const [dragOver, setDragOver] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files).slice(0, 5);
    setFiles((prev) => [...prev, ...dropped].slice(0, 5));
  }, []);

  const handleFileSelect = useCallback((e) => {
    const selected = Array.from(e.target.files).slice(0, 5);
    setFiles((prev) => [...prev, ...selected].slice(0, 5));
  }, []);

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
    setUploadResult(null);
  };

  const uploadFiles = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setUploadResult(null);

    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);

        const encodedName = encodeURIComponent(playerName);
        const resp = await fetch(`/api/player-cards/${encodedName}/upload`, {
          method: 'POST',
          body: formData,
        });

        if (!resp.ok) {
          const errData = await resp.json().catch(() => ({}));
          throw new Error(errData.error?.message || `Upload failed (${resp.status})`);
        }
      }

      setUploadResult({ success: true, message: `${files.length} file(s) uploaded successfully` });
      setFiles([]);
    } catch (err) {
      setUploadResult({ success: false, message: err.message });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
          dragOver
            ? 'border-yellow-500 bg-yellow-500/10'
            : 'border-white/20 hover:border-white/40 bg-white/5'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-300">Drag & drop files here</p>
        <p className="text-xs text-gray-500 mt-1">PDF, images, CSV, video (max 50 MB)</p>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          multiple
          accept=".pdf,.png,.jpg,.jpeg,.csv,.json,.mp4,.mov"
          onChange={handleFileSelect}
        />
      </div>

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((f, i) => (
            <div key={i} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2 border border-white/10">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <span className="text-sm text-white truncate">{f.name}</span>
                <span className="text-xs text-gray-500 flex-shrink-0">
                  {(f.size / 1024 / 1024).toFixed(1)} MB
                </span>
              </div>
              <button onClick={() => removeFile(i)} className="text-gray-500 hover:text-red-400 ml-2">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}

          <button
            onClick={uploadFiles}
            disabled={uploading}
            className="w-full py-2.5 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl text-black font-bold text-sm flex items-center justify-center gap-2 hover:from-yellow-400 hover:to-orange-400 transition-all disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Uploading...' : `Upload ${files.length} File${files.length > 1 ? 's' : ''}`}
          </button>
        </div>
      )}

      {/* Upload result */}
      {uploadResult && (
        <div className={`flex items-center gap-2 p-3 rounded-lg text-sm ${
          uploadResult.success
            ? 'bg-green-500/10 border border-green-500/30 text-green-400'
            : 'bg-red-500/10 border border-red-500/30 text-red-400'
        }`}>
          {uploadResult.success
            ? <CheckCircle className="w-4 h-4 flex-shrink-0" />
            : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
          <span>{uploadResult.message}</span>
        </div>
      )}
    </div>
  );
};

// Chat Tab — embedded per-player chat using Code Mode agent (SSE)
const ChatTab = ({ playerName }) => {
  const [messages, setMessages] = useState([
    { content: `Hi! Ask me anything about ${playerName}'s video analysis, performance, or scouting report.`, sender: 'bot' },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId] = useState(() => `player-card-${Date.now()}`);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMsg = input.trim();
    setMessages((prev) => [...prev, { content: userMsg, sender: 'user' }]);
    setInput('');
    setLoading(true);

    try {
      const resp = await fetch('/api/agent/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: `About ${playerName}: ${userMsg}`,
          sessionId: conversationId,
          properties: {
            sport: 'basketball',
            playerContext: playerName,
          },
        }),
      });

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let botResponse = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'text' && data.content) {
                botResponse += data.content;
              }
            } catch { /* skip non-JSON lines */ }
          }
        }
      }

      setMessages((prev) => [
        ...prev,
        { content: botResponse || 'No response received.', sender: 'bot' },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { content: 'Sorry, I had trouble connecting. Please try again.', sender: 'bot' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[420px]">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-1 mb-3">
        {messages.map((msg, i) => (
          <div key={i} className={msg.sender === 'user' ? 'text-right' : 'text-left'}>
            <div className={`inline-block px-4 py-2 rounded-lg max-w-[85%] text-sm ${
              msg.sender === 'user'
                ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-black'
                : 'bg-gray-800 border border-gray-700 text-gray-200'
            }`}>
              {msg.sender === 'bot' ? (
                <div
                  className="prose prose-invert prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(msg.content)) }}
                />
              ) : msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="text-left">
            <div className="inline-block px-4 py-2 rounded-lg bg-gray-800 border border-gray-700">
              <div className="flex space-x-1 items-center">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about this player..."
          className="flex-1 px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-transparent"
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          className="px-4 py-2 rounded-lg bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold disabled:opacity-50 transition-all hover:from-yellow-400 hover:to-orange-400"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
};

// Social Tab — deep research results
const SocialTab = ({ playerName, researchData, onStartResearch }) => {
  const [researching, setResearching] = useState(false);

  const handleResearch = async () => {
    setResearching(true);
    try {
      await onStartResearch();
    } finally {
      setResearching(false);
    }
  };

  if (researchData?.result) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <Globe className="w-3.5 h-3.5" />
          <span>Deep Research Report</span>
          {researchData.fallback && (
            <span className="px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400 text-[10px]">Fallback</span>
          )}
        </div>
        <div
          className="prose prose-invert prose-sm max-w-none bg-white/5 rounded-xl p-4 border border-white/10 max-h-[400px] overflow-y-auto"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(researchData.result)) }}
        />
      </div>
    );
  }

  if (researchData?.status === 'in_progress') {
    return (
      <div className="text-center py-12">
        <Loader2 className="w-8 h-8 text-yellow-400 animate-spin mx-auto mb-3" />
        <p className="text-sm text-gray-300">Deep research in progress...</p>
        <p className="text-xs text-gray-500 mt-1">This may take a few minutes.</p>
      </div>
    );
  }

  return (
    <div className="text-center py-12">
      <Globe className="w-10 h-10 text-gray-600 mx-auto mb-3" />
      <p className="text-sm text-gray-400 mb-4">
        Run deep research to gather social media presence, NIL deals, and recent news.
      </p>
      <button
        onClick={handleResearch}
        disabled={researching}
        className="px-6 py-2.5 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl text-black font-bold text-sm flex items-center justify-center gap-2 mx-auto hover:from-yellow-400 hover:to-orange-400 transition-all disabled:opacity-50"
      >
        {researching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
        {researching ? 'Starting...' : 'Start Deep Research'}
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN MODAL COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const TAB_CONFIG = [
  { id: 'video', label: 'Video', icon: Video },
  { id: 'stats', label: 'Stats', icon: BarChart3 },
  { id: 'fit', label: 'FIT Score', icon: Zap },
  { id: 'upload', label: 'Upload', icon: Upload },
  { id: 'chat', label: 'Chat', icon: MessageSquare },
  { id: 'social', label: 'Social', icon: Globe },
];

const PlayerCardModal = ({ playerName, team, onClose, isOpen = true }) => {
  const [activeTab, setActiveTab] = useState('video');
  const [card, setCard] = useState(null);
  const [segments, setSegments] = useState([]);
  const [loadingCard, setLoadingCard] = useState(true);
  const [loadingSegments, setLoadingSegments] = useState(true);
  const [researchData, setResearchData] = useState(null);
  const pollIntervalRef = useRef(null);
  const pollTimeoutRef = useRef(null);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  // Fetch card data
  useEffect(() => {
    if (!playerName || !isOpen) return;

    const fetchCard = async () => {
      setLoadingCard(true);
      try {
        const encodedName = encodeURIComponent(playerName);
        const resp = await fetch(`/api/player-cards/${encodedName}`);
        const json = await resp.json();
        if (json.success && json.data?.card) {
          setCard(json.data.card);
        }
      } catch (err) {
        console.error('[PlayerCardModal] Failed to fetch card:', err);
      } finally {
        setLoadingCard(false);
      }
    };

    fetchCard();
  }, [playerName, isOpen]);

  // Fetch segments
  useEffect(() => {
    if (!playerName || !isOpen) return;

    const fetchSegments = async () => {
      setLoadingSegments(true);
      try {
        const encodedName = encodeURIComponent(playerName);
        const resp = await fetch(`/api/player-cards/${encodedName}/segments?limit=100`);
        const json = await resp.json();
        if (json.success && json.data?.segments) {
          setSegments(json.data.segments);
        }
      } catch (err) {
        console.error('[PlayerCardModal] Failed to fetch segments:', err);
      } finally {
        setLoadingSegments(false);
      }
    };

    fetchSegments();
  }, [playerName, isOpen]);

  // Deep research handler
  const handleStartResearch = useCallback(async () => {
    try {
      const encodedName = encodeURIComponent(playerName);
      const resp = await fetch(`/api/player-cards/${encodedName}/research`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await resp.json();
      if (json.success) {
        setResearchData({ status: 'in_progress', interactionId: json.data.interactionId });

        // Poll for completion — store refs for cleanup on unmount
        const pollInterval = setInterval(async () => {
          try {
            const statusResp = await fetch(`/api/player-cards/${encodedName}`);
            const statusJson = await statusResp.json();
            const researchStatus = statusJson.data?.card?.research_status;
            if (researchStatus === 'completed') {
              setResearchData({
                status: 'completed',
                result: statusJson.data.card.social_media_data || 'Research completed. Check player card for results.',
              });
              clearInterval(pollInterval);
              clearTimeout(pollTimeout);
            } else if (researchStatus === 'failed') {
              setResearchData({ status: 'failed', result: 'Research failed. Please try again.' });
              clearInterval(pollInterval);
              clearTimeout(pollTimeout);
            }
          } catch {
            // keep polling
          }
        }, 15000);

        // Stop polling after 10 minutes
        const pollTimeout = setTimeout(() => clearInterval(pollInterval), 600000);

        // Store refs for cleanup
        pollIntervalRef.current = pollInterval;
        pollTimeoutRef.current = pollTimeout;
      }
    } catch (err) {
      console.error('[PlayerCardModal] Research error:', err);
    }
  }, [playerName]);

  // Close on Escape
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  if (!isOpen) return null;

  const grades = card?.grades || {};
  const composite = grades.composite ?? card?.composite_grade ?? null;
  const lenses = ['offensive', 'defensive', 'performance', 'tactical', 'biomechanics', 'shooting'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-gray-950 rounded-2xl border border-white/10 shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="relative px-6 pt-6 pb-4 bg-gradient-to-b from-gray-900 to-gray-950 border-b border-white/10">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all z-10"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-start gap-5">
            {/* Composite grade circle */}
            <RatingCircle
              rating={composite}
              label="Composite"
              size="lg"
              gradientId="composite-grade"
            />

            {/* Player info */}
            <div className="flex-1 min-w-0 pt-1">
              <h2 className="text-xl font-black text-white truncate">
                {playerName || 'Unknown Player'}
              </h2>
              {team && (
                <p className="text-sm text-gray-400 mt-0.5">{team}</p>
              )}

              {/* 6 Lens grades row */}
              <div className="flex gap-3 mt-3 overflow-x-auto pb-1">
                {lenses.map((lens) => {
                  const Icon = LENS_ICONS[lens];
                  const val = grades[lens] ?? (card?.[`${lens}_grade`] ?? null);
                  return (
                    <div key={lens} className="flex flex-col items-center gap-0.5 flex-shrink-0">
                      <RatingCircle
                        rating={val}
                        size="sm"
                        gradientId={`lens-${lens}`}
                      />
                      <div className="flex items-center gap-0.5">
                        <Icon className="w-3 h-3" style={{ color: LENS_COLORS[lens] }} />
                        <span className="text-[9px] text-gray-500 uppercase">{lens.slice(0, 3)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex border-b border-white/10 bg-gray-950/80">
          {TAB_CONFIG.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium transition-all border-b-2 ${
                activeTab === id
                  ? 'border-yellow-500 text-yellow-400'
                  : 'border-transparent text-gray-500 hover:text-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto p-5">
          {activeTab === 'video' && (
            <VideoTab segments={segments} loading={loadingSegments} />
          )}
          {activeTab === 'stats' && (
            <StatsTab card={card} />
          )}
          {activeTab === 'fit' && (
            <FitScoreTab playerName={playerName} />
          )}
          {activeTab === 'upload' && (
            <UploadTab playerName={playerName} />
          )}
          {activeTab === 'chat' && (
            <ChatTab playerName={playerName} />
          )}
          {activeTab === 'social' && (
            <SocialTab
              playerName={playerName}
              researchData={researchData}
              onStartResearch={handleStartResearch}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PlayerCardModal;
